import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { purchaseOrderApi, vendorApi } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Search, Eye, Truck, Package } from 'lucide-react';
import { format } from 'date-fns';

const statusConfig = {
  draft: { label: 'Draft', class: 'status-draft' },
  pending: { label: 'Pending', class: 'status-pending' },
  approved: { label: 'Approved', class: 'status-approved' },
  ordered: { label: 'Ordered', class: 'status-ordered' },
  delivered: { label: 'Delivered', class: 'status-delivered' },
};

export default function PurchaseOrders() {
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVendor, setFilterVendor] = useState('all');

  const fetchData = async () => {
    try {
      const [ordersRes, vendorsRes] = await Promise.all([
        purchaseOrderApi.getAll(),
        vendorApi.getAll()
      ]);
      setOrders(ordersRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Data fetch runs once on mount
  }, []);

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.vendor_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    const matchesVendor = filterVendor === 'all' || order.vendor_id === filterVendor;
    return matchesSearch && matchesStatus && matchesVendor;
  });

  const getStatusCounts = () => {
    const counts = { all: orders.length };
    orders.forEach(o => {
      counts[o.status] = (counts[o.status] || 0) + 1;
    });
    return counts;
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold font-heading">Purchase Orders</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create and manage purchase orders
          </p>
        </div>
        <Link to="/purchase-orders/new">
          <Button className="rounded-sm" data-testid="btn-create-po">
            <Plus className="w-4 h-4 mr-2" />
            Create Order
          </Button>
        </Link>
      </div>

      {/* Status Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'draft', 'pending', 'approved', 'ordered', 'delivered'].map((status) => (
          <Button
            key={status}
            variant={filterStatus === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus(status)}
            className="rounded-sm capitalize"
            data-testid={`filter-status-${status}`}
          >
            {status === 'all' ? 'All' : statusConfig[status]?.label}
            <span className="ml-1.5 text-xs opacity-70">({statusCounts[status] || 0})</span>
          </Button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by PO number or vendor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="po-search"
          />
        </div>
        <Select value={filterVendor} onValueChange={setFilterVendor}>
          <SelectTrigger className="w-[200px] rounded-sm" data-testid="filter-po-vendor">
            <SelectValue placeholder="Filter by vendor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Vendors</SelectItem>
            {vendors.map(vendor => (
              <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <Card className="border border-border shadow-none rounded-sm">
        <CardContent className="p-0">
          {filteredOrders.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">No purchase orders found</p>
              <Link to="/purchase-orders/new">
                <Button variant="link" className="mt-2">
                  Create your first purchase order
                </Button>
              </Link>
            </div>
          ) : (
            <Table data-testid="po-table">
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="font-medium">PO Number</TableHead>
                  <TableHead className="font-medium">Vendor</TableHead>
                  <TableHead className="font-medium">Status</TableHead>
                  <TableHead className="font-medium">Delivery</TableHead>
                  <TableHead className="font-medium">Items</TableHead>
                  <TableHead className="font-medium text-right">Total</TableHead>
                  <TableHead className="font-medium">Date</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow key={order.id} data-testid={`po-row-${order.id}`}>
                    <TableCell className="font-mono-data font-medium">{order.po_number}</TableCell>
                    <TableCell>{order.vendor_name}</TableCell>
                    <TableCell>
                      <Badge className={`${statusConfig[order.status]?.class} rounded-none text-xs uppercase tracking-wider`}>
                        {statusConfig[order.status]?.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        {order.delivery_type === 'delivery' ? (
                          <Truck className="w-4 h-4" />
                        ) : (
                          <Package className="w-4 h-4" />
                        )}
                        <span className="text-xs capitalize">{order.delivery_type}</span>
                        {order.installer_name && (
                          <span className="text-xs">• {order.installer_name}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono-data">{order.items?.length || 0}</TableCell>
                    <TableCell className="text-right font-mono-data font-semibold">
                      ${order.total_amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {order.created_at ? format(new Date(order.created_at), 'dd MMM yyyy') : '-'}
                    </TableCell>
                    <TableCell>
                      <Link to={`/purchase-orders/${order.id}`}>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`btn-view-po-${order.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
